import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import {
    Modal,
    Text,
    Button,
    Group,
    Stack,
    Badge,
    Box,
    Tabs,
    Card,
    Image,
    Anchor,
    ActionIcon,
    Divider,
    ThemeIcon,
    Timeline,
    Alert,
    List,
    Title,
    ScrollArea
} from '@mantine/core';
import {
    IconRocket,
    IconBug,
    IconBulb,
    IconTool,
    IconX,
    IconExternalLink,
    IconCalendar,
    IconInfoCircle
} from '@tabler/icons-react';
import moment from 'moment';
import { logger } from 'utils/logger';

const WhatsNewModal = ({ opened, onClose, versionData, onDismiss }) => {
    const [activeTab, setActiveTab] = useState('features');

    if (!versionData) return null;

    const {
        version,
        releaseDate,
        features = [],
        bugFixes = [],
        improvements = [],
        technicalChanges = []
    } = versionData;

    // Render individual item with rich content support using ReactMarkdown
    const renderItem = (item, index) => {
        const { text, title, description, isImportant, fullText } = item;

        // Use fullText if available (includes sub-items), otherwise use regular text
        const displayText = fullText || text;

        logger.log('displayText', displayText);
        
        const components = {
            h1: ({children}) => {
              const versionMatch = children.toString().match(/Version (.+)/);
              if (versionMatch) {
                return (
                  <Group className="flex items-center justify-between mb-4 pb-4 border-b-2 border-blue-500">
                    <Title order={1}>{children}</Title>
                    <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                      {versionMatch[1]}
                    </span>
                  </Group>
                );
              }
              return <Title order={1}>{children}</Title>;
            },
            
            h2: ({children}) => {
              const sectionIcons = {
                '🚀 New Features': 'bg-green-100 text-green-800',
                '🐛 Bug Fixes': 'bg-red-100 text-red-800',
                '💡 Improvements': 'bg-blue-100 text-blue-800',
                '🔧 Technical Changes': 'bg-purple-100 text-purple-800'
              };
              
              const sectionText = children.toString();
              const colorClass = sectionIcons[sectionText] || 'bg-gray-100 text-gray-800';
              
              return (
                <div className={`${colorClass} rounded-lg p-4 mb-4 mt-6`}>
                  <Title order={2}>{children}</Title>
                </div>
              );
            },
            h3: ({children}) => {
                const sectionIcons = {
                  '🚀 New Features': 'bg-green-100 text-green-800',
                  '🐛 Bug Fixes': 'bg-red-100 text-red-800',
                  '💡 Improvements': 'bg-blue-100 text-blue-800',
                  '🔧 Technical Changes': 'bg-purple-100 text-purple-800'
                };
                
                const sectionText = children.toString();
                // const colorClass = sectionIcons[sectionText] || 'bg-gray-100 text-gray-800';
                const img = (children.length > 0 && children[1].type?.name === 'img') ? children[1] : null;
                // remove the image from the children
                const childrenWithoutImg = img?children[0]?.toString():children;
                var imgElement = null;
                logger.log('img', img);
                logger.log('sectionText', sectionText);
                if (img) {
                    imgElement = <Image src={img.src} />            
                }
                return (
                    <Box mb='md'>
                    <Group spacing="sm" align="center" mb='md' >
                            <Text weight={600} size="sm">
                                {childrenWithoutImg}
                            </Text>

                                <Badge size="xs" variant="filled" color="brand">
                                    New
                                </Badge>
                           </Group>
                           {img}
                           </Box>
               
                );
              },
            
            ul: ({children}) => (
              <List ml='md' mb='md'>{children}</List>
            ),
            
            li: ({children}) => {
                logger.log('children', children);
              const content = children.toString();
              const isMainItem = content.includes('-sub')?false:true;
            //   content.includes('**') || content.includes('    ');
              
              if (isMainItem) {
                return (
                  <List.Item >
                    <Text size='sm'>
                      {children}
                    </Text>
                  </List.Item>
                );
              }
              
              return (
                <List.Item ml={'lg'}>
                  {/* <span className="text-blue-500 mr-2 mt-1">•</span> */}
                  <Text size='sm'>{children.replace('-sub', '')}</Text>
                </List.Item>
              );
            },
            
            strong: ({children}) => (
              <Text weight={600} size='sm'>{children}</Text>
            ),
            
            em: ({children}) => {
              const content = children.toString();
              if (content.includes('Released:')) {
                const dateMatch = content.match(/Released:\s*(.+)/);
                if (dateMatch) {
                  return (
                    <div className="text-gray-600 text-sm mb-6 flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      Released: {dateMatch[1]}
                    </div>
                  );
                }
              }
              return <em className="text-gray-600">{children}</em>;
            },
            
            hr: () => (
              <div className="my-8 border-t border-gray-300"></div>
            ),
            
            p: ({children}) => (
              <Text size="sm">{children}</Text>
            ),
            img: ({src, alt}) => (
                <Image src={src} alt={alt} width={500} />
            )
          };

        return (
            <Card key={index} p="md" withBorder mb="sm">
                <Stack spacing="xs">
                    {/* Title with badge */}
                    {title ? (
                        <Group spacing="sm" align="flex-start">
                            <Text weight={600} size="sm">
                                {title}
                            </Text>
                            {isImportant && (
                                <Badge size="xs" variant="filled" color="brand">
                                    New
                                </Badge>
                            )}
                        </Group>
                    ) : null}
                    
                    {/* Description */}
                    {description && (
                        <Text size="sm" color="dimmed">
                            {description}
                        </Text>
                    )}
                    
                    {/* Markdown content */}
                    <Box>
                        <ReactMarkdown components={components}
                            // components={{
                            //     // Custom component styling
                            //     p: ({ children }) => (
                            //         <Text size="sm" mb="xs">
                            //             {children}
                            //         </Text>
                            //     ),
                            //     ul: ({ children }) => (
                            //         <Box ml="md" mb="xs">
                            //             {children}
                            //         </Box>
                            //     ),
                            //     li: ({ children }) => (
                            //         <Group spacing="xs" align="flex-start" mb={4}>
                            //             <Text size="xs" color="dimmed" mt={2}>-</Text>
                            //             <Box sx={{ flex: 1 }}>
                            //                 <Text size="sm">{children}</Text>
                            //             </Box>
                            //         </Group>
                            //     ),
                            //     strong: ({ children }) => (
                            //         <Text component="span" weight={600}>
                            //             {children}
                            //         </Text>
                            //     ),
                            //     img: ({ src, alt }) => (
                            //         <Box mt="xs">
                            //             <Image
                            //                 src={src}
                            //                 alt={alt}
                            //                 radius="sm"
                            //                 fit="contain"
                            //                 height={200}
                            //                 withPlaceholder
                            //             />
                            //         </Box>
                            //     ),
                            //     a: ({ href, children }) => (
                            //         <Anchor
                            //             href={href}
                            //             target="_blank"
                            //             rel="noopener noreferrer"
                            //             size="sm"
                            //         >
                            //             <Group spacing={4}>
                            //                 <Text>{children}</Text>
                            //                 <IconExternalLink size={12} />
                            //             </Group>
                            //         </Anchor>
                            //     )
                            // }}
                        >
                            {displayText}
                        </ReactMarkdown>
                    </Box>
                </Stack>
            </Card>
        );
    };

    logger.log('features', features);
    // Tab configurations
    const tabs = [
        {
            value: 'features',
            label: 'Features',
            icon: IconRocket,
            items: features,
            color: 'blue',
            emptyMessage: 'No new features in this release.'
        },
        {
            value: 'bugFixes',
            label: 'Bug Fixes',
            icon: IconBug,
            items: bugFixes,
            color: 'red',
            emptyMessage: 'No bug fixes in this release.'
        },
        {
            value: 'improvements',
            label: 'Improvements',
            icon: IconBulb,
            items: improvements,
            color: 'yellow',
            emptyMessage: 'No improvements in this release.'
        },
        // {
        //     value: 'technical',
        //     label: 'Technical',
        //     icon: IconTool,
        //     items: technicalChanges,
        //     color: 'gray',
        //     emptyMessage: 'No technical changes in this release.'
        // }
    ];

    // Filter tabs with content
    const tabsWithContent = tabs.filter(tab => tab.items.length > 0);

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={null}
            size="lg"
            centered
            padding={0}
            withCloseButton={false}
            styles={{
                modal: {
                    maxHeight: '90vh',
                    overflow: 'hidden'
                }
            }}
        >
            {/* Header */}
            <Box p="lg" pb="md" sx={(theme)=>({ borderBottom: theme.colorScheme === 'dark' ? `1px solid ${theme.colors.dark[4]}` : `1px solid ${theme.colors.gray[4]}` })}>
                <Group position="apart" align="flex-start">
                    <Stack spacing="xs">
                        <Group spacing="sm" align="center">
                            <Text size="xl" weight={700}>
                                What's New
                            </Text>
                            <Badge size='md' variant="gradient" gradient={{ from: '#ed6ea0', to: '#ec8c69', deg: 35 }} >
                                v{version}
                            </Badge>
                        </Group>
                        
                        {releaseDate && (
                            <Group spacing="xs" align="center">
                                <IconCalendar size={16} color="#868e96" />
                                <Text size="sm" color="dimmed">
                                    Released {moment(releaseDate).format('MMMM D, YYYY')}
                                </Text>
                            </Group>
                        )}
                    </Stack>
                    
                    <ActionIcon onClick={onClose} size="lg" variant="subtle">
                        <IconX size={18} />
                    </ActionIcon>
                </Group>
            </Box>

            {/* Content */}
            <ScrollArea style={{ maxHeight: 'calc(90vh - 200px)' }}>
            <Box>
                {tabsWithContent.length > 0 ? (
                    <Tabs value={activeTab} onTabChange={setActiveTab}>
                        {/* Tab List */}
                        <Tabs.List px="lg" pt="md">
                            {tabsWithContent.map((tab) => (
                                <Tabs.Tab
                                    key={tab.value}
                                    value={tab.value}
                                    icon={<tab.icon size={16} />}
                                >
                                    <Group spacing="xs">
                                        <Text>{tab.label}</Text>
                                        <Badge size="xs" variant="filled" color={tab.color}>
                                            {tab.items.length}
                                        </Badge>
                                    </Group>
                                </Tabs.Tab>
                            ))}
                        </Tabs.List>

                        {/* Tab Panels */}
                        <Box p="lg" style={{ maxHeight: 'calc(90vh - 280px)', overflowY: 'auto' }}>
                            {tabsWithContent.map((tab) => (
                                <Tabs.Panel key={tab.value} value={tab.value}>
                                    <Stack spacing="md">
                                        {tab.items.length > 0 ? (
                                            tab.items.map((item, index) => renderItem(item, index))
                                        ) : (
                                            <Alert icon={<IconInfoCircle size={16} />} color="gray">
                                                {tab.emptyMessage}
                                            </Alert>
                                        )}
                                    </Stack>
                                </Tabs.Panel>
                            ))}
                        </Box>
                    </Tabs>
                ) : (
                    <Box p="xl">
                        <Alert icon={<IconInfoCircle size={16} />} color="blue">
                            No changes documented for this release.
                        </Alert>
                    </Box>
                )}
            </Box>
            </ScrollArea>
            {/* Footer */}
            <Box p="md" pt="md" sx={(theme)=>({ borderTop: theme.colorScheme === 'dark' ? `1px solid ${theme.colors.dark[4]}` : `1px solid ${theme.colors.gray[4]}` })}>
                <Group position="apart">
                    <Button
                        variant="subtle"
                        color="gray"
                        onClick={() => onDismiss(true)}
                    >
                        Remind me later
                    </Button>
                    
                    <Group spacing="sm">
                        {/* <Button
                            variant="outline"
                            onClick={() => {
                                // Open full changelog in new tab
                                window.open('/changelog', '_blank');
                            }}
                        >
                            View Full Changelog
                        </Button> */}
                        <Button
                            onClick={() => onDismiss(false)}
                        >
                            Got it!
                        </Button>
                    </Group>
                </Group>
            </Box>
        </Modal>
    );
};

export default WhatsNewModal; 